<template>
  <Modal
    name="CREATE_UPDATE_MISSION_MODAL"
    :title="title"
    :width="600"
    actions
  >
    <template v-slot:body>
      <h-loading v-if="isMissionLoading" />

      <v-form
        v-else
        v-model="valid"
        ref="missionForm"
        lazy-validation
      >
        <v-row align="center">
          <v-col cols="8">
            <v-text-field
              v-model="name"
              :label="`${$t('mission.label.name')}`"
              :rules=[rules.required]
              hide-details="auto"
              outlined
            />
          </v-col>
          <v-col cols="4">
            <v-switch
              v-model="active"
              :label="$t(`mission.status.${active}`)"
              hide-details="auto"
              dense
              inset
            />
          </v-col>
        </v-row>

        <v-row>
          <v-col>
            <v-textarea
              v-model="description"
              :label="`${$t('mission.label.description')}`"
              :rules="[rules.required, rules.maxLength(512)]"
              :rows="3"
              hide-details="auto"
              counter="512"
              outlined
            />
          </v-col>
        </v-row>

        <v-row>
          <v-col cols="4" sm="3">
            <v-text-field
              v-model="service_quantity"
              :label="`${$t('mission.label.serviceQuantity')}`"
              hide-details="auto"
              class="amount amount--small"
              type="number"
              outlined
            />
          </v-col>
          <v-col cols="8" sm="3">
            <v-text-field
              v-model.lazy="price"
              v-money="catalogCurrency()"
              :label="$t('mission.label.price')"
              class="amount amount--small inputAmount"
              hide-details="auto"
              ref="price"
              type="tel"
              outlined
            />
          </v-col>
          <v-col cols="12" sm="6">
            <v-select
              v-model="mission_day"
              :label="`${$t('mission.label.missionDay')}`"
              :rules=[rules.required]
              :items="days"
              hide-details="auto"
              item-value="value"
              item-text="label"
              deletable-chips
              small-chips
              clearable
              outlined
              multiple
              chips
            />
          </v-col>
        </v-row>

        <v-row>
          <v-col cols="12" sm="6">
            <div class="d-flex">
              <SelectDate
                v-model="start_date"
                :label="$t('mission.label.startDate')"
                :rules=[rules.required]
                :data="start_date"
                :minDate="today"
              />
              <v-icon color="grey lighten-1" class="mx-1" small>
                mdi-ray-start-arrow
              </v-icon>
              <v-select
                v-model="start_hour"
                :label="$t('mission.label.hour')"
                :disabled="hasNoStartDate"
                :rules=[rules.required]
                :items="hours"
                hide-details="auto"
                class="mw-100"
                outlined
              />
            </div>
          </v-col>
          <v-col cols="12" sm="6">
            <div class="d-flex">
              <SelectDate
                v-model="end_date"
                :label="$t('mission.label.endDate')"
                :disabled="hasNoStartDate"
                :rules=[rules.required]
                :minDate="start_date"
                :data="end_date"
              />
              <v-icon color="grey lighten-1" class="mx-1" small>
                mdi-ray-start-arrow
              </v-icon>
              <v-select
                v-model="end_hour"
                :label="$t('mission.label.hour')"
                :disabled="hasNoEndDate"
                :rules=[rules.required]
                :items="hours"
                hide-details="auto"
                class="mw-100"
                outlined
              />
            </div>
          </v-col>
        </v-row>

        <v-row>
          <v-col>
            <v-autocomplete
              v-model="service_types"
              :label="$t('mission.label.serviceTypes')"
              :no-data-text="$t('table.noResults')"
              :items="appointmentTypes"
              prepend-inner-icon="mdi-magnify"
              hide-details="auto"
              item-value="value"
              item-text="name"
              deletable-chips
              small-chips
              clearable
              outlined
              multiple
              chips
            />
          </v-col>
        </v-row>

        <v-row>
          <v-col>
            <v-autocomplete
              v-model="mission_cities"
              :label="$t('mission.label.cities')"
              :no-data-text="$t('table.noResults')"
              :items="cities"
              prepend-inner-icon="mdi-magnify"
              hide-details="auto"
              item-text="name"
              item-value="id"
              deletable-chips
              small-chips
              clearable
              outlined
              multiple
              chips
            />
          </v-col>
        </v-row>

        <v-row>
          <v-col>
            <v-autocomplete
              v-model="mission_hosts"
              :label="$t('mission.label.hosts')"
              :search-input.sync="search"
              :loading="isSearchingHosts"
              :items="hostSearchResults"
              prepend-inner-icon="mdi-magnify"
              hide-details="auto"
              item-text="name"
              item-value="id"
              deletable-chips
              hide-no-data
              cache-items
              small-chips
              clearable
              outlined
              multiple
              chips
            />
          </v-col>
        </v-row>
      </v-form>
    </template>
    <template v-slot:actions>
      <v-btn
        @click="closeAllModals"
        :disable="isMissionLoading || isMissionSaving"
        class="mr-5"
        color="grey"
        large
        text
      >
        {{ $t('create.cancel') }}
      </v-btn>
      <v-btn
        @click="createUpdate"
        :loading="isMissionLoading || isMissionSaving"
        color="primary"
        elevation="0"
        large
      >
        {{ $t('create.save') }}
      </v-btn>
    </template>
  </Modal>
</template>

<script>
import { VMoney } from 'v-money'
import { currency } from '@/util/enums'
import { clearCurrency } from '@/util/helpers'
import { generateCustomHours } from '@/util/hoursGeneration'
import { mapGetters, mapActions, mapMutations } from 'vuex'
import { required, maxLength } from '@/util/errorHandling'

import moment from 'moment'

import Modal from '@/components/app/Modal'
import SelectDate from '@/components/date/SelectDate'
import SelectHour from '@/components/date/SelectHour'
import { catalogCurrency } from '../../../util/catalog'

export default {
  name: 'MissionCreateUpdate',

  components: {
    Modal,
    SelectDate,
    SelectHour,
  },

  directives: {
    money: VMoney
  },

  computed: {
    ...mapGetters([
      'hosts',
      'cities',
      'mission',
      'isADuplication',
      'isMissionSaving',
      'appointmentTypes',
      'isSearchingHosts',
      'isMissionLoading',
      'hostSearchResults',
    ]),

    hasNoStartDate() {
      return this.start_date === null
    },

    hasNoEndDate() {
      return this.end_date === null
    },

    title() {
      if (this.isMissionLoading) return this.loadingTitle
      if (!this.mission) return this.createTitle
      if (this.isADuplication) return this.duplicateTitle
      if (!this.isADuplication && this.mission?.id) return this.updateTitle
    }
  },

  watch: {
    search(term) {
      if (!term || term.length <= 2) return

      clearTimeout(this.timer)

      this.timer = setTimeout(() => {
        this.fetchHostsBySearchText(term)
      }, 500)
    },

    mission(mission) {
      if (!mission) return
      this.fillFields(mission)
    },
  },

  methods: {
    ...mapMutations(['closeAllModals', 'setHostSearchResults']),

    ...mapActions([
      'saveMission',
      'fetchHostsBySearchText',
      'fetchEnabledCitiesAdmin',
      'fetchAppointmentTypeAsAdmin',
    ]),

    createUpdate() {
      if (!this.$refs.missionForm.validate()) return

      const body = {
        id: this.mission ? this.mission?.id : null,
        name: this.name,
        active: this.active,
        description: this.description,
        service_quantity: this.service_quantity,
        price: clearCurrency(this.price),
        initial: `${this.start_date}T${this.start_hour}:00.000-0300`,
        end: `${this.end_date}T${this.end_hour}:00.000-0300`,
        mission_day: this.mission_day,
        service_types: this.service_types,
        cities: this.mission_cities,
        hosts: this.mission_hosts[0]?.id ? this.mission_hosts.map((host) => host.id) : this.mission_hosts,
      }

      this.$nextTick(() => this.saveMission(body))
    },

    checkDateAndGenerateHours(date, startDelay) {
      const today = isToday(date, startDelay)
      this.otherHours = generateCustomHours(today, 7, 22, this.limitHours)
      this.preferredHours = generatePreferredHours(today, this.limitHours)
    },

    fillFields(mission) {
      this.name = mission.name
      this.active = mission.active
      this.description = mission.description
      this.service_quantity = mission.service_quantity
      this.price = this.regexCurrency.test(mission.price) ? mission.price : mission.price + '.00'
      this.start_date = moment(mission.start_date).format('YYYY-MM-DD')
      this.start_hour = moment(mission.start_date).add(0, 'hours').format('HH:mm')
      this.end_date = moment(mission.end_date).format('YYYY-MM-DD')
      this.end_hour = moment(mission.end_date).add(0, 'hours').format('HH:mm')
      this.mission_day = mission.mission_day
      this.service_types = mission.service_types
      this.mission_cities = mission.cities[0].id ? mission.cities.map((city) => city.id) : mission.cities

      // to field recognize the selected hosts
      this.setHostSearchResults(mission.allowed_hosts)
      this.mission_hosts = mission.allowed_hosts

      this.fillPrice()
    },

    fillPrice() {
      // to fill price at lazy v-model
      if (!this.$refs.price) return
      this.$refs.price.$refs.input.value = this.price
    },

    resetFields() {
      this.name = null
      this.active = true
      this.description = null
      this.service_quantity = 1
      this.price = 0
      this.start_date = null
      this.start_hour = null
      this.end_date = null
      this.end_hour = null
      this.mission_day = []
      this.service_types = []
      this.mission_cities = []
      this.mission_hosts = []
    }
  },

  data() {
    return {
      valid: false,

      loadingTitle: this.$t('forms.loading'),
      createTitle: this.$t('mission.actions.create'),
      updateTitle: this.$t('mission.actions.update'),
      duplicateTitle: this.$t('mission.actions.duplicate'),

      name: null,
      active: true,
      description: null,
      service_quantity: 1,
      price: 0,
      start_date: null,
      start_hour: null,
      end_date: null,
      end_hour: null,
      mission_day: [],
      service_types: [],
      mission_cities: [],
      mission_hosts: [],

      days: [
        { label: this.$t('week.MONDAY'), value: 'MONDAY' },
        { label: this.$t('week.TUESDAY'), value: 'TUESDAY' },
        { label: this.$t('week.WEDNESDAY'), value: 'WEDNESDAY' },
        { label: this.$t('week.THURSDAY'), value: 'THURSDAY' },
        { label: this.$t('week.FRIDAY'), value: 'FRIDAY' },
        { label: this.$t('week.SATURDAY'), value: 'SATURDAY' },
        { label: this.$t('week.SUNDAY'), value: 'SUNDAY' },
      ],

      timer: null,
      hours: null,
      search: null,
      catalogCurrency,
      // currency,

      rules: {
        required,
        maxLength,
      },

      today: moment(this.data).format('YYYY-MM-DD'),
      regexCurrency: /^\d+(?:\.\d{0,2})$/,
    }
  },

  mounted() {
    this.fetchEnabledCitiesAdmin()
    this.fetchAppointmentTypeAsAdmin()
    this.hours = generateCustomHours(false, 7, 22, false)
  },
}
</script>
